'use client'

import { ColumnDef } from '@tanstack/react-table'
import { Ellipsis } from 'lucide-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header'
import { DataTableCheckbox } from '@/components/data-table/data-table-checkbox'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { CustomDomainItem, useRemoveCustomDomain, useVerifyCustomDomain } from '@/lib/api/domains'
import { formatDate } from '@/lib/format'

interface UseDomainsTableColumnsProps {
	onViewDomain: (domain: CustomDomainItem) => void
}

export function useDomainsTableColumns({ onViewDomain }: UseDomainsTableColumnsProps): ColumnDef<CustomDomainItem>[] {
	const { t } = useTranslation('domains')
	const { mutate: verifyDomain } = useVerifyCustomDomain()
	const { mutate: removeDomain } = useRemoveCustomDomain()

	const handleVerifyDomain = (domainId: string) => {
		verifyDomain({ domainId })
	}

	const handleRemoveDomain = (domainId: string) => {
		removeDomain({ domainId })
	}

	return React.useMemo(
		() => [
			{
				id: 'select',
				header: ({ table }) => (
					<Checkbox
						checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
						onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
						aria-label={t('table.selectAll')}
						className="translate-y-0.5"
					/>
				),
				cell: ({ row }) => (
					<DataTableCheckbox row={row} />
				),
				enableSorting: false,
				enableHiding: false,
				size: 40,
			},
			{
				id: 'domain',
				accessorKey: 'domain',
				header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.domain')} />,
				cell: ({ row }) => {
					const domain = row.original
					return <span className="font-medium">{domain.domain}</span>
				},
				enableSorting: true,
				meta: { label: t('table.columns.domain') },
				size: 200,
			},
			{
				id: 'isVerified',
				accessorKey: 'isVerified',
				header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.verificationStatus')} />,
				cell: ({ row }) => {
					const domain = row.original
					return (
						<Badge variant={domain.isVerified ? 'green' : 'yellow'}>
							{domain.isVerified ? t('table.status.verified') : t('table.status.pending')}
						</Badge>
					)
				},
				enableSorting: true,
				meta: { label: t('table.columns.verificationStatus') },
				size: 140,
			},
			{
				id: 'enabled',
				accessorKey: 'enabled',
				header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.status')} />,
				cell: ({ row }) => {
					const domain = row.original
					return (
						<Badge variant={domain.enabled ? 'green' : 'secondary'}>
							{domain.enabled ? t('table.status.active') : t('table.status.inactive')}
						</Badge>
					)
				},
				enableSorting: true,
				meta: { label: t('table.columns.status') },
				size: 100,
			},
			{
				id: 'createdAt',
				accessorKey: 'createdAt',
				header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.created')} />,
				cell: ({ row }) => {
					const domain = row.original
					const date = new Date(domain.createdAt ?? new Date())
					return (
						<div className="flex flex-col gap-0.5 py-1">
							<div className="text-sm leading-none">{formatDate(date, 'MMM d, yyyy')}</div>
							<div className="text-muted-foreground text-xs leading-none">{formatDate(date, 'h:mm a')}</div>
						</div>
					)
				},
				enableSorting: true,
				meta: { label: t('table.columns.created') },
				size: 130,
			},
			{
				id: 'actions',
				cell: ({ row }) => {
					const domain = row.original
					return (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									aria-label={t('table.openMenu')}
									variant="ghost"
									className="data-[state=open]:bg-muted flex h-8 w-8 p-0"
								>
									<Ellipsis className="h-4 w-4" aria-hidden="true" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end" className="w-[160px]">
								<DropdownMenuItem onClick={() => onViewDomain(domain)}>
									{t('table.actions.viewDomain')}
								</DropdownMenuItem>
								{!domain.isVerified && (
									<>
										<DropdownMenuSeparator />
										<DropdownMenuItem onClick={() => handleVerifyDomain(domain.id)}>
											{t('table.actions.verifyDomain')}
										</DropdownMenuItem>
									</>
								)}
								<DropdownMenuSeparator />
								<DropdownMenuItem
									onClick={() => handleRemoveDomain(domain.id)}
									className="text-destructive focus:text-destructive"
								>
									{t('table.actions.removeDomain')}
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					)
				},
				enableSorting: false,
				enableHiding: false,
				size: 40,
			},
		],
		[t, onViewDomain, handleVerifyDomain, handleRemoveDomain]
	)
}

